import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const VendorsGrid = ({ data, onUpdate, onDelete, searchQuery }) => {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  const categoryOptions = [
    { value: 'fiber', label: 'Fiber Materials' },
    { value: 'civil', label: 'Civil Subcontract' },
    { value: 'testing', label: 'Testing Equipment' },
    { value: 'hardware', label: 'IT Hardware' },
    { value: 'licenses', label: 'Software Licenses' },
    { value: 'services', label: 'Professional Services' }
  ];

  const ratingOptions = [
    { value: 5, label: '⭐⭐⭐⭐⭐ Excellent' },
    { value: 4, label: '⭐⭐⭐⭐ Good' },
    { value: 3, label: '⭐⭐⭐ Average' },
    { value: 2, label: '⭐⭐ Below Average' },
    { value: 1, label: '⭐ Poor' }
  ];

  const paymentTermsOptions = [
    { value: 'net30', label: 'Net 30' },
    { value: 'net45', label: 'Net 45' },
    { value: 'net60', label: 'Net 60' },
    { value: 'cod', label: 'Cash on Delivery' },
    { value: 'advance', label: '50% Advance' }
  ];

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const query = searchQuery?.toLowerCase();
    return data?.filter(item => 
      item?.vendorName?.toLowerCase()?.includes(query) ||
      item?.contactPerson?.toLowerCase()?.includes(query) ||
      item?.email?.toLowerCase()?.includes(query) ||
      item?.category?.toLowerCase()?.includes(query)
    );
  }, [data, searchQuery]);

  const handleEdit = (item) => {
    setEditingId(item?.id);
    setEditData(item);
  };

  const handleSave = () => {
    onUpdate(editData);
    setEditingId(null);
    setEditData({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => 
      prev?.includes(id) ? prev?.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows?.length === filteredData?.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData?.map(item => item?.id));
    }
  };

  const getRatingStars = (rating) => {
    return '⭐'?.repeat(rating);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[1400px]">
        <thead>
          <tr className="bg-muted border-b border-border">
            <th className="w-12 px-4 py-3 text-left">
              <input
                type="checkbox"
                checked={selectedRows?.length === filteredData?.length && filteredData?.length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 rounded border-border"
              />
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Vendor Name</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Contact Person</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Email</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Phone</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Category</th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Rating</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Payment Terms</th>
            <th className="w-32 px-4 py-3 text-center text-sm font-semibold text-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData?.map((item) => (
            <tr key={item?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
              <td className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedRows?.includes(item?.id)}
                  onChange={() => handleSelectRow(item?.id)}
                  className="w-4 h-4 rounded border-border"
                />
              </td>
              <td className="px-4 py-3">
                {editingId === item?.id ? (
                  <Input
                    type="text"
                    value={editData?.vendorName}
                    onChange={(e) => setEditData({...editData, vendorName: e?.target?.value})}
                    className="w-full"
                  />
                ) : (
                  <span className="text-sm text-foreground font-medium">{item?.vendorName}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {editingId === item?.id ? (
                  <Input
                    type="text"
                    value={editData?.contactPerson}
                    onChange={(e) => setEditData({...editData, contactPerson: e?.target?.value})}
                    className="w-full"
                  />
                ) : (
                  <span className="text-sm text-muted-foreground">{item?.contactPerson}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {editingId === item?.id ? (
                  <Input
                    type="email"
                    value={editData?.email}
                    onChange={(e) => setEditData({...editData, email: e?.target?.value})}
                    className="w-full"
                  />
                ) : (
                  <span className="text-sm text-muted-foreground">{item?.email}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {editingId === item?.id ? (
                  <Input
                    type="tel"
                    value={editData?.phone}
                    onChange={(e) => setEditData({...editData, phone: e?.target?.value})}
                    className="w-full"
                  />
                ) : (
                  <span className="text-sm text-muted-foreground">{item?.phone}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {editingId === item?.id ? (
                  <Select
                    options={categoryOptions}
                    value={editData?.category}
                    onChange={(value) => setEditData({...editData, category: value})}
                  />
                ) : (
                  <span className="text-sm text-muted-foreground capitalize">{item?.category}</span>
                )}
              </td>
              <td className="px-4 py-3 text-center">
                {editingId === item?.id ? (
                  <Select
                    options={ratingOptions}
                    value={editData?.rating}
                    onChange={(value) => setEditData({...editData, rating: value})}
                  />
                ) : (
                  <span className="text-sm">{getRatingStars(item?.rating)}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {editingId === item?.id ? (
                  <Select
                    options={paymentTermsOptions}
                    value={editData?.paymentTerms}
                    onChange={(value) => setEditData({...editData, paymentTerms: value})}
                  />
                ) : (
                  <span className="text-sm text-foreground uppercase">{item?.paymentTerms}</span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-center gap-2">
                  {editingId === item?.id ? (
                    <>
                      <Button
                        variant="success"
                        size="xs"
                        iconName="Check"
                        onClick={handleSave}
                      />
                      <Button
                        variant="ghost"
                        size="xs"
                        iconName="X"
                        onClick={handleCancel}
                      />
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="xs"
                        iconName="Edit"
                        onClick={() => handleEdit(item)}
                      />
                      <Button
                        variant="ghost"
                        size="xs"
                        iconName="Trash2"
                        onClick={() => onDelete(item?.id)}
                      />
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filteredData?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="FileX" size={48} className="text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground">No vendors found</p>
        </div>
      )}
    </div>
  );
};

export default VendorsGrid;